import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import { DeleteItemIcon } from '@/assets/static/icons/delete-item';
import { DocumentIcon } from '@/assets/static/icons/document';
import { DownloadItemIcon } from '@/assets/static/icons/download-item';
import { OpenFileIcon } from '@/assets/static/icons/open-file';
import { themeVar } from '@/styles/theme';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { EllipsisWithTooltip } from '../ellipsisWithTooltip';
import { Tooltip } from '../tooltip';

interface FileUploadProps {
  fileName: string;
  imageSrc?: string;
  type?: 'document' | 'image';
  additionalContent?: React.ReactNode;
  customActionButtons?: React.ReactNode;
  withHover?: boolean;
  tooltipContent?: ReactNode;
  tooltipWidth?: number;
  openFile?: () => void;
  deleteFile?: () => void;
  downloadFile?: () => void;
  editFile?: () => void;
}

const FileUpload = ({
  fileName,
  imageSrc,
  type = 'document',
  additionalContent,
  customActionButtons,
  withHover = false,
  tooltipContent,
  tooltipWidth = 0,
  openFile,
  deleteFile,
  downloadFile,
  editFile
}: FileUploadProps) => {
  const Content = (
    <FileUploadWrapper withHover={withHover}>
      <FileUploadPreview>
        <FileIconWrapper>
          <DocumentIcon fill={themeVar('font.base')} />
        </FileIconWrapper>
        {type === 'document' && (
          <EllipsisWithTooltip maxContentWidth={680} title={fileName}>
            <FileName cursor={!!openFile ? 'pointer' : 'auto'} onClick={openFile}>
              {fileName}
            </FileName>
          </EllipsisWithTooltip>
        )}
        {type === 'image' && (
          <ImagePreviewWrapper onClick={openFile}>
            <ImagePreview src={imageSrc} alt={fileName} />
          </ImagePreviewWrapper>
        )}
      </FileUploadPreview>

      {additionalContent && <AdditionalContentWrapper>{additionalContent}</AdditionalContentWrapper>}
      <ActionButtons>
        {downloadFile && (
          <DownloadFileIconWrapper onClick={downloadFile}>
            <DownloadItemIcon fill={themeVar('font.action')} />
          </DownloadFileIconWrapper>
        )}
        {openFile && (
          <OpenFileIconWrapper onClick={openFile}>
            <OpenFileIcon fill={themeVar('font.action')} />
          </OpenFileIconWrapper>
        )}
        {editFile && (
          <EditFileIconWrapper onClick={editFile}>
            <EditTodoIcon fill={themeVar('font.action')} />
          </EditFileIconWrapper>
        )}
        {deleteFile && (
          <DeleteFileIconWrapper onClick={deleteFile}>
            <DeleteItemIcon fill={themeVar('context.error')} />
          </DeleteFileIconWrapper>
        )}
        {customActionButtons && customActionButtons}
      </ActionButtons>
    </FileUploadWrapper>
  );

  if (tooltipContent) {
    return (
      <Tooltip title={tooltipContent} maxContentWidth={tooltipWidth} arrow>
        {Content}
      </Tooltip>
    );
  }

  return Content;
};

export default FileUpload;

const FileUploadWrapper = styled.div<{ withHover: boolean }>`
  padding: 10px 20px;
  border-radius: 5px;
  border: 1px solid ${themeVar('menu.active')};
  background: ${themeVar('layer.1')};
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover #infoWrap {
    opacity: 1;
  }

  ${({ withHover }) =>
    withHover
      ? css`
          &:hover {
            background-color: ${themeVar('layer.12')};
            transition: background-color 100ms ease;
          }
        `
      : ''}
`;

const FileUploadPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding-left: 5px;
  border-left: 5px solid ${themeVar('menu.active')};
`;

const FileIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FileName = styled.p<{ cursor: string }>`
  font-size: 16px;
  font-weight: 600;
  line-height: 22.4px;
  cursor: ${({ cursor }) => cursor};
  color: ${themeVar('font.base')};
`;

const ImagePreviewWrapper = styled.div`
  display: block;
  width: 163px;
  height: 100px;
  overflow: hidden;
  cursor: pointer;
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AdditionalContentWrapper = styled.div`
  margin: 0 30px 0 auto;
  padding-left: 20px;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 20px;
`;

const ActionButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 40px;
  width: 40px;
  border-radius: 20px;
  background: ${({ theme }) => themeVar('layer.base')};
`;

const DownloadFileIconWrapper = styled(ActionButtonWrapper)`
  border: 1px solid ${({ theme }) => themeVar('font.action')};
`;

const OpenFileIconWrapper = styled(ActionButtonWrapper)`
  border: 1px solid ${({ theme }) => themeVar('font.action')};
`;

const EditFileIconWrapper = styled(ActionButtonWrapper)`
  border: 1px solid ${({ theme }) => themeVar('font.action')};
`;

const DeleteFileIconWrapper = styled(ActionButtonWrapper)`
  border: 1px solid ${({ theme }) => themeVar('context.error')};
`;
